<!-- Общая навигационная карточка с иконкой, содержимым и дополнительными действиями. -->
<template>
  <div
    class="navigation-card"
    :class="[
      `navigation-card--${layout}`,
      {
        'navigation-card--mobile-status-hidden': mobileStatusHidden,
      },
    ]"
  >
    <button
      class="navigation-card__main"
      type="button"
      @click="$emit('click')"
    >
      <span
        class="navigation-card__icon"
        :class="
          iconText
            ? 'navigation-card__icon--text'
            : `navigation-card__icon--${color}`
        "
        :style="iconStyle"
      >
        <AppIcon v-if="iconName" :name="iconName" :size="18" />
        <span v-else aria-hidden="true">{{ iconText }}</span>
      </span>

      <span v-if="$slots.status" class="navigation-card__status">
        <slot name="status" />
      </span>

      <span class="navigation-card__content">
        <span v-if="eyebrow" class="navigation-card__eyebrow">
          {{ eyebrow }}
        </span>
        <span class="navigation-card__label">{{ label }}</span>
        <span v-if="description" class="navigation-card__description">
          {{ description }}
        </span>
      </span>

      <span v-if="$slots.footer" class="navigation-card__footer-row">
        <span class="navigation-card__footer">
          <slot name="footer" />
        </span>
        <AppIcon
          v-if="withArrow && !listTo"
          class="navigation-card__footer-arrow"
          name="chevronRight"
          :size="16"
        />
      </span>

      <AppIcon
        v-if="withArrow && !listTo && !$slots.footer"
        class="navigation-card__arrow"
        name="chevronRight"
        :size="14"
      />
    </button>

    <BaseTooltip v-if="listTo" :text="listLabel" position="top">
      <button
        class="navigation-card__list-button"
        type="button"
        :aria-label="listLabel"
        @click.stop="openList"
      >
        <AppIcon name="externalLink" :size="18" />
        <span class="navigation-card__list-label">
          <span class="navigation-card__list-action">
            {{ listActionText }}
          </span>
          <span v-if="listSubject" class="navigation-card__list-subject">
            {{ listSubject }}
          </span>
        </span>
      </button>
    </BaseTooltip>
  </div>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";

export default {
  name: "BaseNavigationCard",

  components: {
    AppIcon,
  },

  props: {
    label: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: "",
    },
    color: {
      type: String,
      default: "gray",
    },
    iconBackground: {
      type: String,
      default: "",
    },
    iconName: {
      type: String,
      default: "",
    },
    iconText: {
      type: String,
      default: "",
    },
    eyebrow: {
      type: String,
      default: "",
    },
    layout: {
      type: String,
      default: "compact",
      validator: (value) => ["compact", "catalog"].includes(value),
    },
    withArrow: {
      type: Boolean,
      default: true,
    },
    listTo: {
      type: [Object, String],
      default: null,
    },
    listLabel: {
      type: String,
      default: "Открыть список",
    },
    listActionText: {
      type: String,
      default: "Открыть",
    },
    listSubject: {
      type: String,
      default: "",
    },
    mobileStatusHidden: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["click"],

  computed: {
    iconStyle() {
      if (!this.iconBackground) return null;

      return { background: this.iconBackground };
    },
  },

  methods: {
    openList() {
      if (!this.listTo) return;

      this.$router.push(this.listTo);
    },
  },
};
</script>

<style scoped>
.navigation-card {
  display: flex;
  overflow: hidden;
  width: 100%;
  min-height: 64px;
  align-items: center;
  gap: 8px;
  text-align: left;
  background: #fff;
  border: 1px solid var(--color-grey, #e5e7eb);
  border-radius: 10px;
  transition: box-shadow 0.18s, border-color 0.18s, background 0.18s;
}

.navigation-card:hover {
  background: #fafafa;
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.navigation-card:active {
  background: #f3f4f6;
  box-shadow: none;
}

.navigation-card__main {
  display: flex;
  min-width: 0;
  min-height: 62px;
  align-items: center;
  flex: 1;
  gap: 14px;
  padding: 12px 14px;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.navigation-card__icon {
  display: flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  border-radius: 9px;
}

.navigation-card__icon--indigo {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
}

.navigation-card__icon--blue {
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
}

.navigation-card__icon--cyan {
  background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
}

.navigation-card__icon--teal {
  background: linear-gradient(135deg, #16a34a 0%, #84cc16 100%);
}

.navigation-card__icon--violet {
  background: linear-gradient(135deg, #0ea5e9 0%, #10b981 100%);
}

.navigation-card__icon--gray {
  background: linear-gradient(135deg, #6b7280 0%, #9ca3af 100%);
}

.navigation-card__icon--amber {
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
}

.navigation-card__icon--green {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
}

.navigation-card__icon--red {
  background: #dc2626;
}

.navigation-card__icon--text {
  color: var(--color-dark, #1e1e2f);
  font-size: 20px;
}

.navigation-card__content {
  display: grid;
  min-width: 0;
  flex: 1;
  gap: 2px;
}

.navigation-card__label {
  color: var(--color-dark, #1e1e2f);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
}

.navigation-card__description {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.35;
}

.navigation-card__eyebrow {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.navigation-card__status,
.navigation-card__footer {
  min-width: 0;
}

.navigation-card__arrow {
  flex-shrink: 0;
  color: #666;
}

.navigation-card__list-button {
  display: flex;
  min-width: 64px;
  align-self: stretch;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex-shrink: 0;
  gap: 3px;
  padding: 0 8px;
  color: #6b7280;
  background: transparent;
  border: 0;
  border-left: 1px solid #eef0f3;
  border-radius: 0;
  cursor: pointer;
  transition: background 0.18s, color 0.18s;
}

.navigation-card__list-button:hover {
  color: var(--color-dark, #1e1e2f);
  background: #f3f4f6;
}

.navigation-card__list-label {
  display: grid;
  justify-items: center;
  gap: 1px;
  line-height: 1.05;
  white-space: nowrap;
}

.navigation-card__list-action,
.navigation-card__list-subject {
  color: currentColor;
  font-size: 9px;
  font-weight: 500;
}

.navigation-card__main:focus-visible,
.navigation-card__list-button:focus-visible {
  outline: 2px solid #93c5fd;
  outline-offset: -2px;
}

.navigation-card--catalog {
  min-height: 150px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.navigation-card--catalog .navigation-card__main {
  display: grid;
  min-height: 150px;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    "icon status"
    "content content"
    "footer footer";
  align-items: start;
  gap: 12px 10px;
  padding: 18px 20px;
}

.navigation-card--catalog .navigation-card__icon {
  width: 44px;
  height: 44px;
  grid-area: icon;
  font-size: 22px;
  line-height: 1;
  background: #f6f8fa;
  border-radius: 10px;
}

.navigation-card--catalog .navigation-card__status {
  grid-area: status;
  align-self: start;
  justify-self: end;
}

.navigation-card--catalog .navigation-card__content {
  grid-area: content;
  gap: 4px;
}

.navigation-card--catalog .navigation-card__label {
  font-size: 16px;
  font-weight: 700;
}

.navigation-card--catalog .navigation-card__description {
  color: #64748b;
  font-size: 13px;
  line-height: 1.45;
}

.navigation-card--catalog .navigation-card__footer-row {
  display: flex;
  grid-area: footer;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: auto;
  padding-top: 10px;
  color: #64748b;
  font-size: 13px;
  border-top: 1px solid #eef0f4;
}

.navigation-card--catalog .navigation-card__footer {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.navigation-card--catalog .navigation-card__footer-arrow {
  flex-shrink: 0;
  color: #94a3b8;
}

.navigation-card--catalog .navigation-card__eyebrow {
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

@media (min-width: 769px) {
  .navigation-card--catalog:hover,
  .navigation-card--catalog:focus-within {
    background: #fff;
    border: 1.5px solid #00bfa5;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  }
}

@media (max-width: 600px) {
  .navigation-card--compact {
    min-height: 62px;
    gap: 6px;
  }

  .navigation-card--compact .navigation-card__main {
    min-height: 60px;
    gap: 12px;
    padding: 11px 12px;
  }

  .navigation-card--compact .navigation-card__icon {
    width: 34px;
    height: 34px;
    border-radius: 8px;
  }

  .navigation-card--compact .navigation-card__label {
    font-size: 13px;
  }

  .navigation-card__list-button {
    min-width: 62px;
    padding: 0 7px;
  }
}

@media (max-width: 768px) {
  .navigation-card--catalog {
    min-height: 68px;
    border: 0;
    box-shadow: none;
  }

  .navigation-card--catalog .navigation-card__main {
    min-height: 0;
    grid-template-columns: 40px minmax(0, 1fr) auto 14px;
    grid-template-rows: auto auto;
    grid-template-areas:
      "icon content status arrow"
      "icon footer status arrow";
    align-items: center;
    gap: 3px 12px;
    padding: 14px;
  }

  .navigation-card--catalog .navigation-card__icon {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }

  .navigation-card--catalog .navigation-card__content {
    align-self: end;
    gap: 0;
    margin: 0;
  }

  .navigation-card--catalog .navigation-card__eyebrow,
  .navigation-card--catalog .navigation-card__description {
    display: none;
  }

  .navigation-card--catalog .navigation-card__label {
    overflow: hidden;
    font-size: 15px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .navigation-card--catalog .navigation-card__footer-row {
    display: contents;
  }

  .navigation-card--catalog .navigation-card__footer {
    grid-area: footer;
    align-self: start;
    padding: 0;
    font-size: 12px;
    border: 0;
  }

  .navigation-card--catalog .navigation-card__footer-arrow {
    grid-area: arrow;
    align-self: center;
  }

  .navigation-card--catalog.navigation-card--mobile-status-hidden
    .navigation-card__main {
    grid-template-columns: 40px minmax(0, 1fr) 14px;
    grid-template-areas:
      "icon content arrow"
      "icon footer arrow";
  }

  .navigation-card--mobile-status-hidden .navigation-card__status {
    display: none;
  }
}

@media (max-width: 360px) {
  .navigation-card--catalog:not(.navigation-card--mobile-status-hidden)
    .navigation-card__main {
    grid-template-columns: 40px minmax(0, 1fr) 14px;
    grid-template-areas:
      "icon content arrow"
      "icon footer arrow"
      "icon status arrow";
  }

  .navigation-card--catalog .navigation-card__status {
    justify-self: start;
  }
}
</style>
